import styled from '@emotion/styled';
import { Trans, useLingui } from '@lingui/react/macro';
import { Trash2 } from 'lucide-react';
import { Team } from 'modules/teams/domain/interfaces/team';
import { TeamGroup } from 'modules/championship/domain/interfaces/team-group';
import { deleteStageTeamGroup, upsertStageTeamGroup } from 'modules/championship/store/stages/stages.actions';
import { parseSortOrderInput, sanitizeSortOrderInput } from 'modules/championship/domain/utils/sanitize-sort-order-input';
import { Table } from 'modules/championship/ui/components/form-parts/stages/stage-grid/stage-group/stage-group-table';
import React, { ChangeEvent, ClipboardEvent, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from 'store/store';
import UDInput from 'ud-form/components/input/component';
import { UDButton, UDText } from 'ud-ui/components';
import Modal from 'ud-ui/components/modal';

const sortOrderToInputValue = (sortOrder: number | null): string => (sortOrder === null ? '' : String(sortOrder));

const StateGroupWrapper = styled.div`
  margin-bottom: 10px;
`;

type StageGroupProps = {
  group: TeamGroup;
  teams: Team[];
  externallySelectedTeamIds?: Set<number>;
};

const StageGroup = ({ group, teams, externallySelectedTeamIds = new Set() }: StageGroupProps) => {
  const [groupTeams, setGroupTeams] = useState<(Team | null)[]>([...group.teams, null]);
  const [sortOrderInput, setSortOrderInput] = useState<string>(sortOrderToInputValue(group.sortOrder));
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const dispatch: AppDispatch = useDispatch();
  const { t } = useLingui();
  const groupName = group.name;

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const openDeleteModal = useCallback(() => setIsDeleteModalOpen(true), []);
  const closeDeleteModal = useCallback(() => setIsDeleteModalOpen(false), []);

  const hasChanges = useMemo(() => {
    const originalIds = group.teams.map((t) => t.id).sort((a, b) => a - b);
    const currentIds = groupTeams
      .filter(Boolean)
      .map((t) => t!.id)
      .sort((a, b) => a - b);
    const teamsChanged = originalIds.length !== currentIds.length || originalIds.some((id, i) => id !== currentIds[i]);
    const sortOrderChanged = sortOrderInput !== sortOrderToInputValue(group.sortOrder);

    return teamsChanged || sortOrderChanged;
  }, [group.teams, group.sortOrder, groupTeams, sortOrderInput]);

  const handleSortOrderChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSortOrderInput(sanitizeSortOrderInput(e.target.value));
  }, []);

  const handleSortOrderPaste = useCallback((e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
  }, []);

  const handleSaveClick = useCallback(() => {
    const newGroupTeams = groupTeams.filter((team) => !!team) as Team[];
    const teamIds = newGroupTeams.map((t) => t.id);
    setIsProcessing(true);

    dispatch(
      upsertStageTeamGroup({
        id: group.id,
        stageId: group.stageId,
        name: group.name,
        teamIds,
        sortOrder: parseSortOrderInput(sortOrderInput),
      }),
    ).then(() => {
      setGroupTeams([...newGroupTeams, null]);
      setIsProcessing(false);
    });
  }, [dispatch, group, groupTeams, sortOrderInput]);

  const handleCancelClick = useCallback(() => {
    setGroupTeams([...group.teams, null]);
    setSortOrderInput(sortOrderToInputValue(group.sortOrder));
  }, [group.teams, group.sortOrder]);

  const handleRemoveTeam = useCallback(
    (index: number) => {
      setGroupTeams([...groupTeams.slice(0, index), ...groupTeams.slice(index + 1)]);
    },
    [groupTeams],
  );

  const handleTeamChange = useCallback(
    (index: number, teamId: number) => {
      const team = teams.find((team) => team.id === teamId);
      if (!team) {
        return;
      }

      const newTeams = groupTeams.map((item, i) => (i === index ? team : item));

      if (!groupTeams[index]) {
        newTeams.push(null);
      }

      setGroupTeams(newTeams);
    },
    [groupTeams, teams],
  );

  const handleDeleteStageGroupClick = useCallback(() => {
    setIsProcessing(true);
    return dispatch(deleteStageTeamGroup(group.id)).then(() => {
      setIsProcessing(false);
      closeDeleteModal();
    });
  }, [closeDeleteModal, dispatch, group.id]);

  return (
    <StateGroupWrapper>
      <div className="bg-WhiteGray/10 flex flex-row items-center justify-between rounded-t-lg px-5 py-1.5">
        <UDText variant="body-1">{group.name}</UDText>
        <div className="flex flex-row items-center gap-3">
          <div className="w-40">
            <UDInput
              name="sortOrder"
              inputMode="numeric"
              placeholder={t`Порядковый номер (необязательно)`}
              value={sortOrderInput}
              onChange={handleSortOrderChange}
              onPaste={handleSortOrderPaste}
              disabled={isProcessing}
              showValidateIndicator={false}
            />
          </div>
          <UDButton
            variant={!hasChanges || isProcessing ? 'primary' : 'green'}
            onClick={handleSaveClick}
            disabled={!hasChanges || isProcessing}
          >
            <Trans>Сохранить изменения</Trans>
          </UDButton>
          <UDButton variant="primary" onClick={handleCancelClick} disabled={!hasChanges || isProcessing}>
            <Trans>Отмена</Trans>
          </UDButton>
          <UDButton variant="secondary" onClick={openDeleteModal} disabled={isProcessing} className="flex items-center gap-2">
            <Trans>Удалить группу</Trans> <Trash2 size={16} color="#FF5630" />
          </UDButton>
        </div>
      </div>

      <Table
        isEditMode={true}
        teams={groupTeams}
        stageTeams={teams}
        statistics={group.statistics}
        externallySelectedTeamIds={externallySelectedTeamIds}
        removeTeamClick={handleRemoveTeam}
        teamChange={handleTeamChange}
      />

      <Modal.Confirm
        show={isDeleteModalOpen}
        title={groupName ? t`Удалить группу «${groupName}»?` : t`Удалить группу?`}
        description={t`Это действие необратимо.`}
        isLoading={isProcessing}
        onConfirm={handleDeleteStageGroupClick}
        onCancel={closeDeleteModal}
      />
    </StateGroupWrapper>
  );
};

export default StageGroup;
